/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import {
  Breadcrumbs,
  InputAdornment,
  Box,
  Typography,
  FormControl,
  Select,
  IconButton,
} from "@mui/material";
import {
  GlobalEmployeeSearchPage,
  StyledBox,
  StyledTypography,
  StyledSearchBar,
  StyledDivider,
} from "./styles";
import {
  StyledBreadCrumbContainer,
  StyledInputLabel,
  StyledLink
} from "../employee_visibility/styles";
import SearchIcon from '@mui/icons-material/Search';
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import api from "../../../api";
import authenticatedFetch from "../../../api/authenticatedFetch";
import { Loader } from "../../../components/loader";
import { IndexTable } from "../../../components/index_table";
import camelize from '../../../utils/camelize'
import { useDebounce } from '../../../utils/useDebounce'
import { capitalize, startCase } from 'lodash'
import { useNotificationContext } from "../../../contexts/notificationContext";
import dayjs from "dayjs";
import { buildQueryParams } from '../../../utils/buildQueryParams'
import { RenderDiscountCardsList } from "../../../components/render_discount_cards_list";
import { RenderJobList } from "../../../components/render_job_list";
import { useLocation } from "react-router-dom";
import { ImageViewer } from "../../../components/image_viewer_modal";
import { CertificateList } from "../../../components/certificates_list";
import { useCertificateViewer } from "../../../hooks/useCertificateViewer";
import { FilterContainer } from "../../../components/filter_container";
import { UpdateButton } from "../../../components/update_button";
import { selectBaseStyles, viewTypographyStyles } from "../styles";
import DropdownSearchBar from "../../../components/dorpdown_searchbar";
import { SelectedMenuItem } from "../../../components/selected_menu_item";
import { ResetButton } from "../../../components/reset_button";
import ClearIcon from '@mui/icons-material/Clear';
import { SOURCE_OF_TRUTH_OPTIONS, STATUS_OPTIONS } from "../../../constants/filterOptions";
import { StyledMenuItem } from "../../../components/styled_menu_item";
import { Query_Keys } from "../../../lib/queryKeys";
import { containsText, matchesNormalizedSearch } from "../../../utils/normalizeSearchTerm";

const ALL_VALUE = 'All';
const EMPTY_FILTERS = {
  source_of_truth: ALL_VALUE,
  status: ALL_VALUE,
}
const DEFAULT_PAGINATION = {
  page: 1,
  itemsPerPage: 25,
}
const DEFAULT_SORT = {
  sortBy: '',
  sortOrder: 'asc',
}

export const GlobalEmployeeSearch = () => {
  const [page, setPage] = useState(DEFAULT_PAGINATION.page)
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGINATION.itemsPerPage)
  const [sortBy, setSortBy] = useState(DEFAULT_SORT.sortBy)
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT.sortOrder)
  const [search, setSearch] = useState('')
  const [csvLoading, setCsvLoading] = useState(false)
  const showNotification = useNotificationContext();
  const location = useLocation()
  const debouncedSearchTerm = useDebounce(location?.state?.user ? location?.state?.user : search, 300);
  const [imageUrl, setImageUrl] = useState('')
  const [imageViewerOpen, setImageViewerOpen] = useState(false)
  const { imageLoading, handleCertificateDisplay } = useCertificateViewer({
    showNotification,
    onImageOpen: (url) => {
      setImageUrl(url)
      setImageViewerOpen(true)
    },
  })
  const [filtersChanged, setFiltersChanged] = useState(false)
  const initialFilters = {
    source_of_truth: location?.state?.source_of_truth || ALL_VALUE,
    status: location?.state?.status || ALL_VALUE,
  }
  const [sourceOfTruth, setSourceOfTruth] = useState(ALL_VALUE)
  const [status, setStatus] = useState(ALL_VALUE)
  const [sourceOfTruthSearch, setSourceOfTruthSearch] = useState("")
  const [statusSearch, setStatusSearch] = useState("")
  const [appliedFilters, setAppliedFilters] = useState(initialFilters)

  useEffect(() => {
    if (location?.state?.user) {
      setSearch(location?.state?.user)
    }
  }, [location])

  const fetchNetworkGuests = async () => {
    const response = await api.get('/v1/admin/network_guests/global_search', {
      params: {
        per_page: itemsPerPage,
        page: page,
        search: debouncedSearchTerm,
        source_of_truth: appliedFilters?.source_of_truth === ALL_VALUE ? undefined : appliedFilters?.source_of_truth,
        status: appliedFilters?.status === ALL_VALUE ? undefined : appliedFilters?.status,
        sort_by: sortBy || undefined,
        sort_order: sortBy ? sortOrder : undefined
      }
    })
    return { data: response?.data, pageCount: response?.headers["total-count"] }
  }

  const { data, isFetching } = useQuery({
    queryKey: Query_Keys.global_employee_search({
      queryFilter: appliedFilters,
      search: debouncedSearchTerm,
      page: page,
      itemsPerPage: itemsPerPage,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchNetworkGuests,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData
  })

  const filteredSourceOfTruth = useMemo(() =>
    SOURCE_OF_TRUTH_OPTIONS?.filter((source_of_truth) => {
      return matchesNormalizedSearch(source_of_truth?.label, sourceOfTruthSearch);
    }), [SOURCE_OF_TRUTH_OPTIONS, sourceOfTruthSearch]
  )

  const filteredStatus = useMemo(() =>
    STATUS_OPTIONS?.filter((statusOption) => {
      return matchesNormalizedSearch(statusOption?.label, statusSearch);
    }), [statusSearch]
  )

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);
      const query = buildQueryParams({ 
        search: search, 
        source_of_truth: sourceOfTruth === ALL_VALUE ? undefined : sourceOfTruth,
        status: status === ALL_VALUE ? undefined : status
      })
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=global_search&${query}`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date
      let filename = `global_search_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('CSV download error:', error);
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  }
  const handleReset = useCallback(() => {
    setFiltersChanged(false);
    setSourceOfTruth(ALL_VALUE);
    setStatus(ALL_VALUE);
    setSearch('');
    setAppliedFilters(EMPTY_FILTERS);
    setPage(DEFAULT_PAGINATION.page);
    setItemsPerPage(DEFAULT_PAGINATION.itemsPerPage);
    setSortBy(DEFAULT_SORT.sortBy);
    setSortOrder(DEFAULT_SORT.sortOrder);
  }, []);

  const handleChange = () => {
    setItemsPerPage(DEFAULT_PAGINATION.itemsPerPage);
    setPage(DEFAULT_PAGINATION.page);
    setAppliedFilters({
      source_of_truth: sourceOfTruth === ALL_VALUE ? undefined : sourceOfTruth,
      status: status === ALL_VALUE ? undefined : status,
    })
    setFiltersChanged(false);
  }

  const handleSourceOfTruthChange = (event) => {
    setFiltersChanged(true)
    setSourceOfTruth(event.target.value)
  };

  const handleSourceOfTruthClear = () => {
    setSourceOfTruth(ALL_VALUE)
    setFiltersChanged(true);
  }

  const handleStatusChange = (event) => {
    setFiltersChanged(true)
    setStatus(event.target.value)
  };

  const handleStatusClear = () => {
    setStatus(ALL_VALUE)
    setFiltersChanged(true);
  }

  const handleSearch = (searchQuery) => {
    setSearch(searchQuery)
    setPage(1)
  }

  const handleSort = (columnKey, orderDirection) => {
    setSortBy(columnKey)
    setSortOrder(orderDirection)
    setPage(1)
  }

  const RenderList = (data) => {
    return (
      <Box>
        <ul style={{ padding: 0 }}>
          {data?.map((item) => (
            <li key={item?.id} style={{ listStyle: "none" }}>
              {startCase(capitalize(item?.name))}
            </li>
          ))}
        </ul>
      </Box>
    )
  }

  const RenderData = (data) => {
    return (
      <Box>
        <ul style={{ padding: 0 }}>
          {data?.map((item, id) => (
            <li key={id} style={{ listStyle: "none" }}>
              {item ? item : '-'}
            </li>
          ))}
        </ul>
      </Box>
    )
  }

  const RenderNumberData = (data) => {
    if (data?.length === 0) {
      return '$0.00'
    }

    return (
      <Box>
        <ul style={{ padding: 0 }}>
          {data?.map((item, id) => (
            <li key={id} style={{ listStyle: "none" }}>
              {item ? `$${Number(item).toFixed(2)}` : '$0.00'}
            </li>
          ))}
        </ul>
      </Box>
    )
  }

  const tableColumns = [
    { key: "id", sortable: true },
    { key: "name", sortable: true },
    { key: "phone_number", sortable: true },
    { key: "status", sortable: true },
    { key: "job_end_date", sortable: true },
    { key: "restaurants", sortable: true },
    { key: "availed_discount", label: "Available Perq", sortable: true },
    { key: "discount_cards", label: "Perq Cards", sortable: true },
    { key: "number_of_group_cards", label: "Group Cards", sortable: true },
    { key: "job_descriptions", sortable: true },
    { key: "pay_basis", sortable: true },
    { key: "wages", sortable: true },
    { key: "source_of_truth", sortable: true },
    { key: "certificates", sortable: true },
  ];

  const columns = tableColumns?.map((column) => {
    return {
      key: column.key,
      label: column.label || camelize(column.key),
      sortable: column.sortable,
    };
  });

  const TableRows = (data) => {
    return data?.map((record) => {
      const groupedDiscountCardsForTooltip = Object.entries(record?.group_assigned_discount_cards || {}).map(
        ([cardName, cardDetails]) => ({
          name: cardName,
          color: cardDetails?.color,
          restaurant_names: [
            ...(cardDetails?.restaurant_names || []),
            ...(cardDetails?.restaurant_sub_group_names || []),
          ].filter((v, i, arr) => arr.indexOf(v) === i),
        })
      );

      return {
        id: record?.id,
        name: record?.full_name,
        phone_number: record?.phone_number,
        status: capitalize(record?.status),
        job_end_date: record?.job_end_date && dayjs(record?.job_end_date).format('MM/DD/YYYY'),
        restaurants: {
          display: (
            RenderList(record?.restaurants)
          ),
          sortValue: record?.restaurants?.[0]?.name
        },
        availed_discount: record?.availed_discount ? `$${Number(record?.availed_discount).toFixed(2)}` : '$0.00',
        discount_cards: {
          display: (
            <RenderDiscountCardsList
              data={record?.discount_cards}
            />
          ),
          sortValue: record?.discount_cards?.length
        },
        number_of_group_cards: {
          display: (
            <RenderDiscountCardsList
              data={groupedDiscountCardsForTooltip}
              showRestaurantNames
              showCountOnHover
              countValue={record?.number_of_group_cards ?? 0}
              popoverTitle="Group Card Assignments"
              popoverSubtitle={`${record?.full_name || ""} · All locations`.trim()}
              maxHeight="220px"
              maxWidth="150px"
            />
          ),
          sortValue: record?.number_of_group_cards ?? 0,
        },
        job_descriptions: {
          display: (
            <RenderJobList
              data={record?.job_descriptions}
            />
          ),
          sortValue: record?.job_descriptions?.[0]?.title
        },
        pay_basis: {
          display: (
            RenderData(record?.pay_basis)
          ),
          sortValue: record?.pay_basis?.[0]
        },
        wages: {
          display: (
            RenderNumberData(record?.wages)
          ),
          sortValue: record?.wages?.[0] ||  '0.00'
        },
        source_of_truth: {
          display: (
            RenderData(record?.source_of_truth?.map((item) => startCase(item)))
          ),
          sortValue: record?.source_of_truth?.[0]
        },
        certificates: {
          display: (
            <CertificateList
              data={record?.certificates}
              imageLoading={imageLoading}
              onCertificateClick={handleCertificateDisplay}
            />
          ),
          sortValue: record?.certificates?.[0]?.certificate_name
        },
      };
    });
  };

  const rows = TableRows(data?.data);

  return (
    <GlobalEmployeeSearchPage>
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
          <StyledLink color={"#515152"}>Reports</StyledLink>
          <StyledLink color={"#515152"}>Employee Reports</StyledLink>
          <StyledLink color={"#515152"}>Employee Search</StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      <FilterContainer>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", gap: "18px" }}>
            <Typography
              variant="body2"
              sx={{
                ...viewTypographyStyles,
                alignSelf: { xs: "flex-start", sm: "center" },
                whiteSpace: "nowrap",
                mt: 0
              }}
            >
              View
            </Typography>

            <FormControl
              size="small"
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { xs: "100%", sm: "120px" }
              }}
            >
              <StyledInputLabel>Source of Truth</StyledInputLabel>
              <Select
                value={sourceOfTruth || ALL_VALUE}
                renderValue={() =>
                  startCase(sourceOfTruth)
                }
                size="small"
                label="Source of Truth"
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: { xs: "100%", sm: "140px" },
                  borderColor: "#2b4fb9",
                }}
                onChange={handleSourceOfTruthChange}
                onClose={() => setTimeout(() => setSourceOfTruthSearch(''), 1000)}
                endAdornment={
                  sourceOfTruth !== ALL_VALUE ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        onClick={handleSourceOfTruthClear}
                      >
                        <ClearIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    </InputAdornment>
                  ) : (
                    null
                  )
                }
              >
                <DropdownSearchBar changeSearchText={setSourceOfTruthSearch} placeholder={'Search source of truth'} />
                {/* All option */}
                <StyledMenuItem
                  value={ALL_VALUE}
                >
                  {sourceOfTruth === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                </StyledMenuItem>
                {
                  filteredSourceOfTruth
                    ?.filter((source_of_truth) =>
                      containsText(
                        `${source_of_truth?.label}`,
                        sourceOfTruthSearch
                      )
                    )
                    .map((item) => (
                      <StyledMenuItem
                        key={item?.value}
                        value={item?.value}
                      >
                        {
                          sourceOfTruth === item?.value ? <SelectedMenuItem data={item?.label} /> : item?.label
                        }
                      </StyledMenuItem>
                    ))
                }
              </Select>
            </FormControl>

            {/* Status Filter */}
            <FormControl
              size="small"
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { xs: "100%", sm: "120px" }
              }}
            >
              <StyledInputLabel>Status</StyledInputLabel>
              <Select
                value={status || ALL_VALUE}
                renderValue={() =>
                  capitalize(status)
                }
                size="small"
                label="Status"
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: { xs: "100%", sm: "140px" },
                  borderColor: "#2b4fb9",
                }}
                onChange={handleStatusChange}
                onClose={() => setTimeout(() => setStatusSearch(''), 1000)}
                endAdornment={
                  status !== ALL_VALUE ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        onClick={handleStatusClear}
                      >
                        <ClearIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    </InputAdornment>
                  ) : (
                    null
                  )
                }
              >
                <DropdownSearchBar changeSearchText={setStatusSearch} placeholder={'Search status'} />
                {/* All option */}
                <StyledMenuItem
                  value={ALL_VALUE}
                >
                  {status === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                </StyledMenuItem>
                {
                  filteredStatus
                    ?.filter((statusOption) =>
                      containsText(
                        `${statusOption?.label}`,
                        statusSearch
                      )
                    )
                    .map((item) => (
                      <StyledMenuItem
                        key={item?.value}
                        value={item?.value}
                      >
                        {
                          status === item?.value ? <SelectedMenuItem data={item?.label} /> : item?.label
                        }
                      </StyledMenuItem>
                    ))
                }
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", gap: "10px" }}>

            <ResetButton
              onClick={handleReset}
              status={isFetching}
            />

            <UpdateButton
              onClick={handleChange}
              filtersChanged={filtersChanged}
              status={isFetching}
            />
          </Box>

        </Box>
      </FilterContainer>

      <StyledBox report={false}>
        <StyledTypography>Employee Search</StyledTypography>
        <StyledSearchBar
          value={search}
          placeholder="Search"
          InputProps={{
            startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
          }}
          onChange={(e) => handleSearch(e.target.value)}
          autoComplete="off"
        />
      </StyledBox>
      <StyledDivider />

      {
        isFetching && !search ? (
          <Loader />
        ) : (
          <IndexTable
            columns={columns}
            data={rows}
            tableId="employee-search-entries"
            type="entries"
            serverSort={true}
            sortBy={sortBy}
            sortOrder={sortOrder}
            defaultSortBy={sortBy}
            defaultSortOrder={sortOrder}
            onSort={handleSort}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            pageCount={data?.pageCount}
            report={false}
            searchLoading={isFetching && !!debouncedSearchTerm}
            hasSearchTerm={!!debouncedSearchTerm}
            showDownloadButton={true}
            disableDownloadButton={data?.data.length === 0 || isFetching || csvLoading}
            handleExportCsv={handleExportCsv}
            csvLoading={csvLoading}
          />
        )
      }

      <ImageViewer
        open={imageViewerOpen}
        imageUrl={imageUrl}
        alt={'Certificate'}
        onClose={() => {
          setImageViewerOpen(false)
          setImageUrl(null)
        }}
      />
    </GlobalEmployeeSearchPage>
  )
}
